import { api } from './apiClient';

interface PaginatedResponse<T> {
  total: number;
  limit: number;
  skip: number;
  data: T[];
}

export interface User {
  id: number;
  email: string;
  googleId: string;
  auth0Id: string;
  companyId: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  lastLoggedIn: string;
  expiresAt: string;
  isActive: boolean;
  subscriptionPlan: string;
  isAdmin: boolean;
  companyName: string;
}

export interface CreateUserParams {
  firstName: string;
  lastName: string;
  email: string;
  companyId: number;
  isActive?: boolean;
  isAdmin: boolean;
  expiresAt?: number;
}

export interface RefreshTokenResponse {
  accessToken: string;
  user: User;
}

export interface AvatarResult {
  id: number;
  avatarUrl: string | null;
}

export const usersService = {
  uploadAvatar: async (id: number, file: File): Promise<AvatarResult> => {
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      file.size <= 0 ||
      file.size > 2 * 1024 * 1024
    ) {
      throw new Error('Choose a JPEG, PNG, or WebP image no larger than 2 MB');
    }
    const upload = await api.post<{ url: string; key: string }>(
      '/user-avatars',
      {
        userId: id,
        fileType: file.type,
        fileSize: file.size,
      }
    );
    if (!upload?.url || !upload.key)
      throw new Error('Could not start the avatar upload');
    const response = await fetch(upload.url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!response.ok)
      throw new Error('Avatar upload failed. Please try again.');
    const result = await api.patch<AvatarResult>(`/user-avatars/${id}`, {
      key: upload.key,
    });
    if (!result?.avatarUrl) throw new Error('Could not save the avatar');
    return result;
  },

  removeAvatar: async (id: number): Promise<AvatarResult> => {
    const result = await api.delete<AvatarResult>(`/user-avatars/${id}`);
    if (!result || result.id !== id)
      throw new Error('Could not remove the avatar');
    return result;
  },

  // User Operations
  getUsers: async (): Promise<PaginatedResponse<User>> => {
    const response = await api.get<PaginatedResponse<User>>(
      '/users?isActive=true&$limit=1000&$skip=0'
    );
    return response;
  },

  getUser: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response;
  },

  createUser: async (user: CreateUserParams) => {
    return api.post<User & { invitationEmailSent?: boolean }>('/users', user);
  },

  updateUser: async (id: number, user: CreateUserParams) => {
    return api.patch(`/users/${id}`, user);
  },

  archiveUser: async (id: number): Promise<User> => {
    return api.patch(`/users/${id}`, { isActive: false });
  },

  updatePassword: async (id: number, password: string): Promise<User> => {
    return api.patch(`/users/${id}`, { password });
  },

  // Authentication Operations
  refreshToken: async (accessToken: string): Promise<RefreshTokenResponse> => {
    return api.post('/authentication', {
      strategy: 'refresh',
      accessToken,
    });
  },
};
